export const HOME_COLUMNS = 12;
export const HOME_MAX_ROWS = 1000;
export type HomeColumns = 3 | 6 | 9 | 12;
export type HomeRows = 1 | 2 | 3 | 4;
export interface HomeSize {
  columns: HomeColumns;
  rows: HomeRows;
}
export interface HomePlacement extends HomeSize {
  id: string;
  column: number;
  row: number;
}
export interface HomeCell {
  column: number;
  row: number;
}
export type HomeSizesById = Readonly<Record<string, readonly HomeSize[]>>;
export function validHomeSize(size: HomeSize): boolean {
  return [3, 6, 9, 12].includes(size.columns) && [1, 2, 3, 4].includes(size.rows);
}
export function readingOrder(layout: readonly HomePlacement[]): HomePlacement[] {
  return [...layout].sort((a, b) => a.row - b.row || a.column - b.column || a.id.localeCompare(b.id));
}
export function overlaps(a: HomePlacement, b: HomePlacement): boolean {
  return (
    a.column < b.column + b.columns &&
    a.column + a.columns > b.column &&
    a.row < b.row + b.rows &&
    a.row + a.rows > b.row
  );
}
export function validateHomeLayout(layout: readonly HomePlacement[], sizes?: HomeSizesById): string | null {
  if (layout.length > 100) return 'В одном обзоре доступно до 100 виджетов.';
  const ids = new Set<string>();
  for (const item of layout) {
    if (!item.id.trim() || ids.has(item.id))
      return 'Идентификаторы виджетов должны быть непустыми и уникальными.';
    ids.add(item.id);
    if (
      !validHomeSize(item) ||
      !Number.isInteger(item.column) ||
      !Number.isInteger(item.row) ||
      item.column < 0 ||
      item.column + item.columns > HOME_COLUMNS ||
      item.row < 0 ||
      item.row + item.rows > HOME_MAX_ROWS
    )
      return 'Положение или размер виджета вне сетки.';
    if (
      sizes &&
      (!Object.hasOwn(sizes, item.id) ||
        !sizes[item.id].some((size) => size.columns === item.columns && size.rows === item.rows))
    )
      return 'Виджет не поддерживает выбранный размер.';
  }
  for (let i = 0; i < layout.length; i++)
    for (let j = i + 1; j < layout.length; j++)
      if (overlaps(layout[i], layout[j])) return 'Виджеты пересекаются.';
  return null;
}
export function findFirstFree(
  layout: readonly HomePlacement[],
  size: HomeSize,
  from: HomeCell = { column: 0, row: 0 },
): HomeCell {
  if (!validHomeSize(size)) throw Error('Неизвестный размер виджета.');
  for (let row = Math.max(0, Math.round(from.row)); row + size.rows <= HOME_MAX_ROWS; row++)
    for (
      let column = row === from.row ? Math.max(0, Math.round(from.column)) : 0;
      column + size.columns <= HOME_COLUMNS;
      column++
    ) {
      const candidate = { id: '', ...size, column, row };
      if (!layout.some((item) => overlaps(item, candidate))) return { column, row };
    }
  throw Error('В сетке нет свободного места.');
}
export function addHomeWidget(layout: readonly HomePlacement[], id: string, size: HomeSize): HomePlacement[] {
  if (layout.length >= 100) throw Error('В одном обзоре доступно до 100 виджетов.');
  if (!id.trim() || layout.some((item) => item.id === id)) throw Error('Виджет с таким id уже добавлен.');
  return [...layout.map((item) => ({ ...item })), { id, ...size, ...findFirstFree(layout, size) }];
}
export function compactHomeLayout(layout: readonly HomePlacement[], anchorId?: string): HomePlacement[] {
  const anchor = layout.find((item) => item.id === anchorId),
    placed: HomePlacement[] = anchor ? [{ ...anchor }] : [];
  for (const item of readingOrder(layout).filter((item) => item.id !== anchorId)) {
    let row = 0;
    while (row < item.row && placed.some((other) => overlaps({ ...item, row }, other))) row++;
    while (placed.some((other) => overlaps({ ...item, row }, other))) row++;
    placed.push({ ...item, row });
  }
  return layout.map((item) => placed.find((next) => next.id === item.id)!);
}
export function placeHomeWidget(
  layout: readonly HomePlacement[],
  id: string,
  target: HomeCell & Partial<HomeSize>,
  compact = true,
  insert = false,
): HomePlacement[] {
  const source = layout.find((item) => item.id === id);
  if (!source) return layout.map((item) => ({ ...item }));
  const columns = target.columns ?? source.columns,
    rows = target.rows ?? source.rows;
  if (!validHomeSize({ columns, rows })) throw Error('Неизвестный размер виджета.');
  const active: HomePlacement = {
    ...source,
    columns,
    rows,
    column: Math.max(0, Math.min(HOME_COLUMNS - columns, Math.round(target.column))),
    row: Math.max(0, Math.min(HOME_MAX_ROWS - rows, Math.round(target.row))),
  };
  const placed = [active];
  for (const item of readingOrder(layout).filter((item) => item.id !== id)) {
    let row = item.row,
      column = item.column;
    if (insert && item.row === active.row) {
      let candidate = column,
        collisions = placed.filter((other) => overlaps({ ...item, column: candidate }, other));
      while (collisions.length) {
        candidate = Math.max(...collisions.map((other) => other.column + other.columns));
        collisions = placed.filter((other) => overlaps({ ...item, column: candidate }, other));
      }
      if (candidate + item.columns <= HOME_COLUMNS) column = candidate;
    }
    while (placed.some((other) => overlaps({ ...item, column, row }, other))) row++;
    if (row + item.rows > HOME_MAX_ROWS) throw Error('В сетке нет свободного места.');
    placed.push({ ...item, column, row });
  }
  const next = layout.map((item) => placed.find((other) => other.id === item.id)!);
  return compact ? compactHomeLayout(next, id) : next;
}
/** A gesture always resolves from its start layout, including an exact return to its origin. */
export function previewHomeWidget(
  base: readonly HomePlacement[],
  id: string,
  target: (HomeCell & Partial<HomeSize>) | null,
  compact = true,
): HomePlacement[] {
  const source = base.find((item) => item.id === id);
  if (!source || !target) return base.map((item) => ({ ...item }));
  const columns = target.columns ?? source.columns,
    rows = target.rows ?? source.rows,
    column = Math.max(0, Math.min(HOME_COLUMNS - columns, Math.round(target.column))),
    row = Math.max(0, Math.min(HOME_MAX_ROWS - rows, Math.round(target.row)));
  if (source.column === column && source.row === row && source.columns === columns && source.rows === rows)
    return base.map((item) => ({ ...item }));
  return placeHomeWidget(base, id, { column, row, columns, rows }, compact, true);
}
export function closestHomeSize(columns: number, rows: number, allowed: readonly HomeSize[]): HomeSize {
  if (!allowed.length || allowed.some((size) => !validHomeSize(size)))
    throw Error('Допустимые размеры заданы неверно.');
  return [...allowed].sort(
    (a, b) =>
      Math.abs(a.columns - columns) / 3 +
      Math.abs(a.rows - rows) -
      (Math.abs(b.columns - columns) / 3 + Math.abs(b.rows - rows)),
  )[0];
}
export function nextHomeSize(
  current: HomeSize,
  dx: number,
  dy: number,
  allowed: readonly HomeSize[],
): HomeSize {
  const candidates = allowed.filter(
    (size) =>
      (!dx || Math.sign(size.columns - current.columns) === Math.sign(dx)) &&
      (!dy || Math.sign(size.rows - current.rows) === Math.sign(dy)),
  );
  return candidates.length
    ? closestHomeSize(current.columns + dx * 3, current.rows + dy, candidates)
    : { columns: current.columns, rows: current.rows };
}
export function reorderHomeLayout(
  layout: readonly HomePlacement[],
  id: string,
  direction: -1 | 1,
): HomePlacement[] {
  const ordered = readingOrder(layout),
    index = ordered.findIndex((item) => item.id === id),
    to = index + direction;
  if (index < 0 || to < 0 || to >= ordered.length) return layout.map((item) => ({ ...item }));
  [ordered[index], ordered[to]] = [ordered[to], ordered[index]];
  const placed: HomePlacement[] = [];
  let from: HomeCell = { column: 0, row: 0 };
  for (const item of ordered) {
    const cell = findFirstFree(placed, item, from);
    placed.push({ ...item, ...cell });
    from = cell;
  }
  return placed;
}
export function homeRows(layout: readonly HomePlacement[]): number {
  return Math.max(1, ...layout.map((item) => item.row + item.rows));
}
